import { useEffect, useRef, useState } from "react"

import { Alert, App, Flex, Tabs, Typography } from "antd"
import cls from "classnames"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"

import Button from "../../../../Components/Button"
import TextArea from "../../../../Components/Input/TextArea"
import Spinner from "../../../../Components/Spinner"
import { RoutesPaths } from "../../../../constants/user-routes"
import Icon from "../../../../Icon"
import { approveEvidence, generateEvidence, getDispute } from "../../api/services"
import { selectDetailAction } from "../../redux/disputes.slice"
import { getDisputesStatistics, rejectEvidence, reopenDispute } from "../../redux/disputes.thunks"
import DetailsTab from "./components/DetailsTab/DetailsTab"
import DetailTopbar from "./components/DetailTopbar/DetailTopbar"
import EnrichmentPane from "./components/EnrichmentPane/EnrichmentPane"
import EvidencePane from "./components/EvidencePane/EvidencePane"
import {
  ENRICHMENT_FIELD_COUNT,
  enrichmentBand,
  enrichmentFillCount,
  enrichmentFillPct
} from "./schema/enrichment"
import { buildStatusInfo } from "./utils/statusInfo"

import styles from "./detail.module.less"

const { Text } = Typography

// Owns the dispute payload and the four decisions that can be taken on it (approve,
// give up, reopen, regenerate). Everything a single tab needs and nothing else does
// lives in that tab's component; what several of them read stays here.
const DetailPage = () => {
  const { dispute_id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { modal, notification } = App.useApp()

  const [detail, setDetail] = useState(null)
  // Page tabs: Details (the dispute fields) / Enrichment (the per-dispute facts) /
  // Evidence PDF (the document preview). The primary CTA lives in the topbar so it
  // stays reachable from every tab.
  const [activeTab, setActiveTab] = useState("details")
  const [loadError, setLoadError] = useState(false)
  const [version, setVersion] = useState(null)
  const [actioning, setActioning] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  // Unsaved enrichment edits. Gates the "Save & Update PDF" bar so it only offers a
  // regenerate when there is actually something new to put in the PDF; cleared once
  // the render lands (or comes back already up to date).
  const [enrichmentDirty, setEnrichmentDirty] = useState(false)

  const dispatch = useDispatch()
  // reject/reopen run through the slice; this carries the saga result back so the
  // component can show its toast + reload the detail.
  const detailAction = useSelector(selectDetailAction)
  // Auto-submission lead time, straight from the merchant's Preferences, so the date
  // shown here can't contradict "Evidence is sent N days before the deadline".
  const autoSubmitDaysBefore = useSelector(
    (st) => st.disputes?.profile?.preferences?.days_before_due ?? 2
  )

  const reload = async () => {
    setLoadError(false)
    // The mock backend has nowhere to persist Enrichment-tab edits (there is no
    // real endpoint for them yet), so any re-fetch would otherwise reseed them
    // back to the demo default and silently discard whatever the merchant just
    // typed. Carry the current in-memory values forward across every reload
    // (approve, reject, reopen, regenerate, and the auto-generate fallback below
    // all call getDispute + setDetail here).
    const priorEnrichment = detail?.enrichment_fields
    // Same story for the evidence-images list: the merchant's manual uploads only
    // live in memory, so a re-fetch must not reseed the list back to the prepared
    // demo set and drop them.
    const priorImages = detail?.evidence_images
    const withPriorEnrichment = (data) => {
      let next = data
      if (priorEnrichment)
        next = { ...next, enrichment_fields: { ...next.enrichment_fields, ...priorEnrichment } }
      if (priorImages) next = { ...next, evidence_images: priorImages }
      return next
    }
    const res = await getDispute(dispute_id)
    if (!res?.ok || !res?.data) {
      // 404 / network: surface a not-found state instead of an endless spinner.
      setLoadError(true)
      return
    }
    setDetail(withPriorEnrichment(res.data))
    const v = res.data.latest_version
    const status = res.data?.dispute?.status
    // A dispute we never submitted evidence for (Skipped) keeps the "No evidence was
    // submitted" empty state authoritative, even if a draft EvidenceFile was
    // generated earlier (on a prior view or via Regenerate) and never cleared:
    // don't expose its pages, or the page would show a full evidence PDF while its
    // own pill/sub-line say no evidence was submitted.
    setVersion(v && status !== "skipped" ? v : null)
    if (v || !["needs_response", "awaiting_review"].includes(status)) return
    // Auto-generate a first version ONLY for disputes still awaiting action, which
    // includes a reopened second cycle (it owes a fresh response, and its action bar
    // is enabled, so it needs a draft to act on). A resolved/skipped/submitted dispute
    // with no evidence should stay empty, not fabricate a PDF that contradicts its
    // own status. EvidencePane loads the pages off the version we set here.
    const gen = await generateEvidence(dispute_id)
    if (gen?.ok && gen?.data?.version) {
      // Legacy synchronous response.
      const re = await getDispute(dispute_id)
      if (re?.ok && re?.data) setDetail(withPriorEnrichment(re.data))
      setVersion(gen.data.version)
    } else if (gen?.ok && (gen.status === 202 || gen?.data?.status === "queued")) {
      // Async: poll for the first version.
      const out = await pollNewVersion(dispute_id, 0)
      if (out.status === "new") {
        const re = await getDispute(dispute_id)
        if (re?.ok && re?.data) setDetail(withPriorEnrichment(re.data))
        setVersion(out.version)
      }
    }
  }

  useEffect(() => {
    document.title = "Dispute details - Chargeback"
  }, [])

  useEffect(() => {
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispute_id])

  const dispute = detail?.dispute
  const recommendation = detail?.recommendation
  const activity = detail?.activity
  const commissionPreview = detail?.commission_preview
  const commissionCharge = detail?.commission_charge

  // Enrichment edits are local-only (no endpoint yet): both writers patch the detail
  // copy in place and arm the "Save & Update PDF" bar, which is what turns them into a
  // new PDF.
  const handleEnrichmentChange = (key, value) => {
    setDetail((prev) =>
      prev
        ? { ...prev, enrichment_fields: { ...(prev.enrichment_fields || {}), [key]: value } }
        : prev
    )
    setEnrichmentDirty(true)
  }
  const handleEvidenceImagesChange = (next) => {
    setDetail((prev) => (prev ? { ...prev, evidence_images: next } : prev))
    setEnrichmentDirty(true)
  }
  const handleAssigneeChange = (assignee) =>
    setDetail((prev) =>
      prev ? { ...prev, dispute: { ...prev.dispute, assigned_to: assignee } } : prev
    )

  // Submission runs as an async task after approve returns 200, so the API
  // alone can't tell us if it actually reached the issuer. Poll the dispute
  // until its status advances out of the review states (or evidence_provided
  // flips), so the UI reflects the REAL submission outcome rather than an
  // optimistic guess.
  const pollSubmitted = async (id, tries = 8, delayMs = 1200) => {
    for (let i = 0; i < tries; i++) {
      await new Promise((r) => setTimeout(r, delayMs))
      const res = await getDispute(id)
      const d = res?.data
      const st = d?.dispute?.status
      if (
        d &&
        (d.dispute?.evidence_provided || ["pending_decision", "won", "lost_defended"].includes(st))
      ) {
        return d
      }
    }
    return null
  }

  const handleApprove = () => {
    modal.confirm({
      title: t("Submit this evidence now?"),
      icon: null,
      content: (
        <Flex vertical gap={8}>
          <Text type="secondary">
            {t(
              "Approving sends the evidence to the issuer straight away, not at the scheduled time. Any auto-send scheduled for this dispute is cancelled."
            )}
          </Text>
          <Text type="secondary">
            {t(
              "The PDF is locked once it is sent, so check it reads the way you want before you approve. This cannot be undone."
            )}
          </Text>
        </Flex>
      ),
      okText: t("Approve & submit now"),
      cancelText: t("Cancel"),
      centered: true,
      onOk: async () => {
        if (!version) {
          notification.error({ message: t("Approve failed") })
          return
        }
        setActioning(true)
        setSubmitting(true)
        try {
          // Approve the version actually on screen, not a silent fallback to the
          // newest file.
          const r = await approveEvidence(dispute_id, version)
          if (r?.status === 409) {
            // A newer version exists; approving this older one would silently no-op.
            const lv = r?.data?.latest_version
            notification.warning({
              message: t("Approve the latest version"),
              description: t(
                "A newer version exists. Switch to the latest version, then approve and submit."
              )
            })
            // Drop the action spinners before switching to the latest version so the
            // Approve button doesn't keep spinning behind the warning while the canvas
            // swaps (EvidencePane reloads its pages off the new version).
            setActioning(false)
            setSubmitting(false)
            if (lv) setVersion(lv)
            return
          }
          if (!r?.ok) {
            notification.error({ message: t("Approve failed") })
            return
          }
          // Approved server-side; now confirm the async submission actually landed.
          const updated = await pollSubmitted(dispute_id)
          if (updated) {
            setDetail(updated)
            if (updated.latest_version) setVersion(updated.latest_version)
            notification.success({
              message: t("Submitted to issuer"),
              description: t('Dispute moved to "Pending decision".')
            })
          } else {
            notification.warning({
              message: t("Approved, submission in progress"),
              description: t(
                "We approved the evidence and are sending it to the issuer. This is taking longer than usual; the status will update here shortly."
              )
            })
            reload()
          }
          // Refresh the list statistics + sidebar badge so they don't lag the detail.
          dispatch(getDisputesStatistics())
        } catch (e) {
          console.error("Approve error:", e)
          notification.error({ message: t("Approve failed") })
        } finally {
          setActioning(false)
          setSubmitting(false)
        }
      }
    })
  }

  const handleReject = () => {
    let notesValue = ""
    // An inquiry is answered rather than fought, so the wording follows the case type
    // (the behaviour is identical: nothing is submitted and the case becomes Skipped).
    const inquiry = dispute?.dispute_type === "inquiry"
    modal.confirm({
      title: inquiry ? t("Don't respond to this inquiry?") : t("Stop fighting this dispute?"),
      icon: null,
      centered: true,
      width: 480,
      okButtonProps: { danger: true },
      okText: inquiry ? t("Don't respond") : t("Stop fighting"),
      cancelText: t("Cancel"),
      content: (
        <Flex vertical gap={12}>
          {/* One short line per consequence. Names the two statuses the merchant will
              actually see on the dispute, so the outcome is not a surprise later. */}
          <Flex vertical gap={4}>
            <Text type="secondary">{t("We won't submit any evidence.")}</Text>
            <Text type="secondary">
              {inquiry
                ? t("The inquiry becomes Skipped, and may escalate into a dispute later.")
                : t("The dispute becomes Skipped, and may become Lost later.")}
            </Text>
            <Text type="secondary">{t("No success fee is charged.")}</Text>
          </Flex>
          <TextArea
            rows={3}
            placeholder={t("Optional notes, visible to your team only.")}
            onChange={(e) => {
              notesValue = e.target.value
            }}
          />
        </Flex>
      ),
      onOk: () => {
        if (!version) {
          notification.error({ message: t("Could not close the dispute") })
          return
        }
        setActioning(true)
        dispatch(rejectEvidence({ id: dispute_id, version, notes: notesValue }))
      }
    })
  }

  const handleReopen = () => {
    setActioning(true)
    dispatch(reopenDispute({ id: dispute_id }))
  }

  // Bridge the reject/reopen saga results back to the component-scoped toast + detail
  // reload (toasts go through the page's own `notification`; the detail is local state).
  // Fires once per result (lastResult is a fresh object on each Success/Failure, null on
  // Request).
  const lastResultRef = useRef(null)
  useEffect(() => {
    const res = detailAction?.lastResult
    // Only consume a result THIS detail instance is mid-action for (actioning). A stale
    // slice result from another dispute survives a remount, so without this guard it
    // would fire a wrong "Dispute closed/reopened" toast on a dispute merely opened to view.
    if (!res || res === lastResultRef.current || !actioning) return
    lastResultRef.current = res
    if (res.verb === "reject") {
      setActioning(false)
      if (res.ok) {
        notification.success({ message: t("Dispute closed") })
        reload()
      } else {
        notification.error({ message: t("Could not close the dispute") })
      }
    } else if (res.verb === "reopen") {
      setActioning(false)
      if (res.ok) {
        notification.success({
          message: t("Dispute reopened"),
          description: t("It's back in your review queue.")
        })
        reload()
      } else {
        notification.error({
          message: t("Could not reopen the dispute"),
          description: res.data?.detail || ""
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailAction?.lastResult])

  // Poll the detail endpoint until a NEW evidence version appears (used after the
  // async 202 Regenerate). A content-hash no-op never bumps the version, so after
  // the window we treat it as "already up to date" rather than an error.
  const pollNewVersion = async (id, prevVersion, tries = 14, delayMs = 1200) => {
    for (let i = 0; i < tries; i++) {
      await new Promise((r) => setTimeout(r, delayMs))
      const res = await getDispute(id)
      const v = res?.data?.latest_version || 0
      if (res?.ok && v > (prevVersion || 0)) return { status: "new", data: res.data, version: v }
    }
    return { status: "noop" }
  }

  const upToDate = () =>
    notification.info({
      message: t("Already up to date"),
      description: t(
        "This PDF already matches your current Configure answers. Edit Configure, then regenerate to update it."
      )
    })

  // `onSuccess` fires once the PDF is confirmed current, either a genuinely new
  // version or a no-op "already up to date". The "Save & Update PDF" bar passes it
  // to jump the merchant to Evidence PDF and close the edit-then-recheck loop.
  const handleRegenerate = async ({ onSuccess } = {}) => {
    setActioning(true)
    setRegenerating(true)
    try {
      const prev = version || 0
      const r = await generateEvidence(dispute_id)
      if (r?.ok && r?.data?.version) {
        // Legacy SYNCHRONOUS response (backend rendered inline). Kept so frontend
        // and backend can deploy in either order.
        if (r.data.unchanged) {
          upToDate()
        } else {
          notification.success({
            message: t("New version generated"),
            description: `v${r.data.version}`
          })
          reload()
        }
        // Either way the PDF now reflects what is on screen, so the edits are no
        // longer pending.
        setEnrichmentDirty(false)
        onSuccess?.()
      } else if (r?.ok && (r.status === 202 || r?.data?.status === "queued")) {
        // ASYNC: the render was enqueued. poll for the new version.
        const prevV = typeof r?.data?.previous_version === "number" ? r.data.previous_version : prev
        const out = await pollNewVersion(dispute_id, prevV)
        if (out.status === "new") {
          notification.success({
            message: t("New version generated"),
            description: `v${out.version}`
          })
          reload()
        } else {
          upToDate()
        }
        setEnrichmentDirty(false)
        onSuccess?.()
      } else if (r?.status === 400) {
        notification.error({ message: t("Regenerate failed"), description: r?.data?.detail })
      } else {
        notification.error({ message: t("Regenerate failed") })
      }
    } finally {
      setActioning(false)
      setRegenerating(false)
    }
  }
  if (loadError) {
    return (
      <Flex vertical align="center" justify="center" gap={16} style={{ minHeight: "60vh" }}>
        <Text type="secondary">
          {t("We couldn't load this dispute. It may have been removed.")}
        </Text>
        <Button
          type="primary"
          onClick={() => navigate(RoutesPaths.recover.all)}
          text={t("Back to all disputes")}
        />
      </Flex>
    )
  }

  if (!detail) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: "60vh" }}>
        <Spinner />
      </Flex>
    )
  }

  const status = dispute?.status
  // Evidence is actionable (give up / regenerate / approve) only before it's submitted:
  // needs_response and awaiting_review, which a reopened second cycle comes back as.
  // Once submitted (pending_decision) or resolved (won/lost) the action bar is hidden.
  const isActionable = ["needs_response", "awaiting_review"].includes(status)
  // When the evidence goes out automatically, for still-actionable disputes: the
  // backend's scheduled_send_at when queued; otherwise derived from the merchant's own
  // auto-submission setting (days_before_due), so this date always matches what
  // Preferences promises rather than a hardcoded day-before.
  const autoSubmitAt =
    isActionable && (dispute?.scheduled_send_at || dispute?.due_by)
      ? dayjs.utc(
          dispute.scheduled_send_at ||
            dayjs.utc(dispute.due_by).subtract(autoSubmitDaysBefore, "day")
        )
      : null

  const recoveredAmount = status === "won" ? Number(dispute.amount) : 0
  const commissionAmount =
    status === "won" && commissionCharge
      ? Number(commissionCharge.amount)
      : Number(commissionPreview?.amount || 0)

  // Has the issuer escalated this dispute past the first round?
  const stage = dispute?.cycle_stage
  const escalated = stage === "pre_arbitration" || stage === "arbitration"
  const stageLabel = stage === "arbitration" ? t("Arbitration") : t("Pre-arbitration")

  const statusInfo = buildStatusInfo({
    status,
    escalated,
    stageLabel,
    commissionCharge,
    recoveredAmount,
    commissionAmount,
    t
  })

  const enrichmentValues = detail?.enrichment_fields || {}
  const enrichmentPctValue = enrichmentFillPct(enrichmentValues)

  return (
    <div className={styles.page}>
      <DetailTopbar
        dispute={dispute}
        escalated={escalated}
        stageLabel={stageLabel}
        isActionable={isActionable}
        actioning={actioning}
        submitting={submitting}
        onReject={handleReject}
        onApprove={handleApprove}
        onReopen={handleReopen}
      />

      {/* Label-only items, content rendered below; the same Tabs pattern the
          Integrations page uses. */}
      <Tabs
        className={styles.tabs}
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: "details", label: t("Details") },
          {
            key: "enrichment",
            label: (
              <span className={styles.tabLabel}>
                {t("Enrichment")}
                <span className={cls(styles.tabPill, styles[enrichmentBand(enrichmentPctValue)])}>
                  {Math.round(enrichmentPctValue)}%
                </span>
                <Text type="secondary" className={styles.tabCount}>
                  {t("_n_/_total_", {
                    n: enrichmentFillCount(enrichmentValues),
                    total: ENRICHMENT_FIELD_COUNT
                  })}
                </Text>
              </span>
            )
          },
          { key: "evidence", label: t("Evidence PDF") }
        ]}
      />

      {/* Merchant-responsibility notice. Its own strip between the tab bar and the
          scrolling body (not inside it, which is a flex row), so it shows on every
          tab in every dispute status and stays put while the tab content scrolls. */}
      <div className={styles.notice}>
        <Alert
          type="warning"
          showIcon
          icon={<Icon type="alert_triangle_error" style={{ fontSize: 20 }} />}
          message={<Text strong>{t("Pay attention")}</Text>}
          description={
            <Text>
              {t(
                "You're responsible for the accuracy and truthfulness of your evidence and edits. We prepare your dispute response, but you're responsible for the final submission."
              )}
            </Text>
          }
        />
      </div>

      <div className={styles.body}>
        <DetailsTab
          active={activeTab === "details"}
          dispute={dispute}
          disputeId={dispute_id}
          status={status}
          statusInfo={statusInfo}
          enrichmentBreakdown={detail?.enrichment_detail || null}
          recommendation={recommendation}
          activity={activity}
          escalated={escalated}
          stageLabel={stageLabel}
          isActionable={isActionable}
          autoSubmitAt={autoSubmitAt}
          onAssigneeChange={handleAssigneeChange}
        />
        <EnrichmentPane
          active={activeTab === "enrichment"}
          enrichmentValues={enrichmentValues}
          onEnrichmentChange={handleEnrichmentChange}
          evidenceImages={detail?.evidence_images || []}
          onEvidenceImagesChange={handleEvidenceImagesChange}
          isActionable={isActionable}
          enrichmentDirty={enrichmentDirty}
          actioning={actioning}
          regenerating={regenerating}
          onSave={() => handleRegenerate({ onSuccess: () => setActiveTab("evidence") })}
        />
        <EvidencePane
          active={activeTab === "evidence"}
          disputeId={dispute_id}
          disputeStatus={status}
          version={version}
          onVersionChange={setVersion}
          evidenceFiles={detail?.evidence_files || []}
          isActionable={isActionable}
        />
      </div>
    </div>
  )
}

export default DetailPage
